import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Button, Alert } from 'react-bootstrap';
import axios from 'axios';
import Layout from '../../components/layouts/Main';
import config from '../../config';
import { toast } from 'react-toastify';
import BrokerTable from '../../components/brokers/BrokerTable';
import BrokerModal from '../../components/brokers/BrokerModal';
import { PulseLoader } from "react-spinners";
import { useHasPermission, PERMISSIONS } from '../../utils/permissions';

function BrokerPage() {
  const [brokers, setBrokers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Permission checks
  const canViewBrokers = useHasPermission(PERMISSIONS.VIEW_BROKERS);
  const canAddBrokers = useHasPermission(PERMISSIONS.ADD_BROKERS);
  const canEditBrokers = useHasPermission(PERMISSIONS.EDIT_BROKERS);
  const canDeleteBrokers = useHasPermission(PERMISSIONS.DELETE_BROKERS);

  const fetchBrokers = useCallback(async () => {
    if (!canViewBrokers) {
      toast.error("You don't have permission to view brokers");
      return;
    }

    setIsLoading(true);
    try {
      const res = await axios.get(`${config.API_BASE_URL}/brokers`);
      setBrokers(res.data);
    } catch (err) {
      console.error("Error fetching brokers", err);
      toast.error("Failed to load brokers");
    } finally {
      setIsLoading(false);
    }
  },[canViewBrokers]);

  useEffect(() => {
    fetchBrokers();
    if (sessionStorage.getItem('brokerAdded')) {
      toast.success('Broker added successfully!');
      sessionStorage.removeItem('brokerAdded');
    }
  }, [canViewBrokers, fetchBrokers]);

  const handleBrokerAdded = () => {
    if (!canAddBrokers) {
      toast.error("You don't have permission to add brokers");
      return;
    }
    window.location.reload();
  };

  // If user can't view brokers, show permission denied
  if (!canViewBrokers) {
    return (
      <Layout>
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
            <li className="breadcrumb-item active">Brokers</li>
          </ol>
        </nav>
        <Alert variant="danger" className="d-flex align-items-center">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <div>
            <strong>Access Denied</strong>
            <p className="mb-0">You don't have permission to view brokers. Contact your administrator for access.</p>
          </div>
        </Alert>
      </Layout>
    );
  }

  return (
    <Layout>
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
          <li className="breadcrumb-item active">Brokers</li>
        </ol>
      </nav>

      <Row className="mb-4">
        <Col>
          <h2>Brokers</h2>
          <small className="text-muted">
            {canEditBrokers ? "View and manage" : "View"} brokers
            {!canAddBrokers && !canEditBrokers && !canDeleteBrokers && " (read-only)"}
          </small>
        </Col>
        <Col className="text-end">
          {/* <PermissionGate
            require={PERMISSIONS.ADD_BROKERS}
            fallback={
              <Button variant="outline-primary" disabled title="You don't have permission to add brokers">
                <i className="fas fa-plus me-2" /> Add Broker
              </Button>
            }
          > */}
          {canAddBrokers && (
            <Button onClick={() => setShowModal(true)}>
              <i className="fas fa-plus me-2" /> Add Broker
            </Button>
          )}
          {/* </PermissionGate> */}
        </Col>
      </Row>

      {isLoading ? (
        <PulseLoader
          color="#014b7cbb"
          size={9}
        />
      ) : (
        <BrokerTable 
          brokers={brokers} 
          canEdit={canEditBrokers}
          canDelete={canDeleteBrokers}
          canView={canViewBrokers}
        />
      )}

      {canAddBrokers && (
        <BrokerModal
          show={showModal}
          onHide={() => setShowModal(false)}
          onBrokerAdded={handleBrokerAdded}
        />
      )}
    </Layout>
  );
}

export default BrokerPage;
